import { useWatch, type Control } from 'react-hook-form'
import { Cartao } from '../../../components/ListaDeDados'
import { cn } from '../../../lib/cn'
import { CamposDoPlano } from './CamposDoPlano'
import { ARTEFATOS_EXIGIDOS, artefatosPreenchidos } from './pendencias'
import type { FormularioNovaGmud, PropsDosCampos } from './esquema'

interface Props extends PropsDosCampos {
  control: Control<FormularioNovaGmud>
}

const TOTAL = ARTEFATOS_EXIGIDOS.length

/**
 * A largura da barra, uma classe por valor possível.
 *
 * São sete estados, não um contínuo — dá para escrevê-los. A alternativa seria `style={{width}}`,
 * e CSS inline não existe nesta aplicação (só o relatório de impressão tem folha própria): é ele
 * que escapa do `tailwind-merge`, ignora o tema e reaparece divergente na tela seguinte.
 */
const LARGURA_DA_BARRA: Record<number, string> = {
  0: 'w-0',
  1: 'w-1/6',
  2: 'w-2/6',
  3: 'w-3/6',
  4: 'w-4/6',
  5: 'w-5/6',
  6: 'w-full',
}

/**
 * Quanto do plano já está pronto para o comitê.
 *
 * Componente separado porque é ele, e só ele, que precisa acompanhar cada tecla digitada. Se a
 * conta ficasse na etapa, o `useWatch` redesenharia os oito campos de texto a cada letra.
 *
 * @param props o controle do formulário
 * @returns a barra de progresso do plano
 */
function ProgressoDoPlano({ control }: { control: Control<FormularioNovaGmud> }) {
  const valores = useWatch({ control })
  const preenchidos = artefatosPreenchidos(valores)
  const percentual = Math.round((preenchidos / TOTAL) * 100)

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-slate-800">
          {preenchidos} de {TOTAL} itens exigidos pelo comitê
        </span>
        <span className="text-xs text-slate-500">{percentual}%</span>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={TOTAL}
        aria-valuenow={preenchidos}
        aria-valuetext={`${preenchidos} de ${TOTAL} itens exigidos pelo comitê`}
        className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200"
      >
        <div
          aria-hidden="true"
          className={cn('h-full rounded-full bg-laranja-500 transition-all', LARGURA_DA_BARRA[preenchidos])}
        />
      </div>
    </div>
  )
}

/**
 * Etapa 2 — o plano da mudança.
 *
 * O aviso vem antes dos campos, e é calmo de propósito: a pessoa está diante de oito caixas de
 * texto longo, e a primeira pergunta que ela faz é "preciso preencher tudo isso agora?". A
 * resposta é não — e é a mesma regra do backend (`CriarGmudRequest`: tudo do plano é opcional),
 * não uma gentileza da tela. Quem cobra o plano completo é a transição para o comitê, com a lista
 * do que falta.
 *
 * @param props registro do formulário, erros e o controle
 * @returns o conteúdo da segunda etapa
 */
export function EtapaPlano({ register, errors, control }: Props) {
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-niteroi-100 bg-niteroi-50 p-4 text-sm text-niteroi-900">
        <p>
          <strong className="font-semibold">Nada aqui é obrigatório para salvar.</strong> A solicitação nasce como
          rascunho e você pode continuar depois — enquanto ela não for enviada ao comitê, ninguém além de você a vê.
        </p>
        <p className="mt-2">
          O que faltar é cobrado na hora de enviar ao comitê, com a lista exata do que preencher. Os itens marcados
          abaixo como exigidos são os que o comitê precisa ter em mãos para decidir.
        </p>
      </div>

      <Cartao titulo="Plano da mudança" descricao="Escreva para quem não acompanha o dia a dia do sistema.">
        <div className="space-y-5">
          <ProgressoDoPlano control={control} />
          <CamposDoPlano register={register} errors={errors} control={control} />
        </div>
      </Cartao>
    </div>
  )
}
